/**
 * ReadinessMilestones — the "what did we do to get here?" timeline on
 * the Resilience page. Renders the last N readiness milestones as a
 * vertical timeline with a coloured chip per kind.
 *
 * Reads from GET /api/dora/readiness-milestones. Soft-fails silently
 * (returns null) if the endpoint 4xxs or the tenant has no history yet.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Milestone } from "lucide-react";

const TONE_CLS = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  purple: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
};

function fmtDate(iso) {
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export function ReadinessMilestones() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/dora/readiness-milestones?limit=15")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  const items = data?.milestones || [];
  if (items.length === 0) return null;
  const meta = data?.kind_meta || {};

  return (
    <section className="mt-10" data-testid="readiness-milestones">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ek-purple/10 text-ek-purple">
          <Milestone size={16} />
        </span>
        <div>
          <h3 className="ek-eyebrow">Readiness milestones</h3>
          <p className="text-sm font-semibold text-ek-ink">
            What we did in the last 30 days to get here
          </p>
        </div>
      </div>
      <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
        The material changes behind the trend line — RTO reductions, tolerance
        tightening, gaps closed and new services in-scope. Board- and
        regulator-defensible narrative for the readiness improvement.
      </p>

      <ol className="mt-6 relative border-l-2 border-ek-purple/20 pl-6 space-y-5">
        {items.map((m) => {
          const km = meta[m.kind] || { label: m.kind, tone: "purple" };
          return (
            <li
              key={m.milestone_id || `${m.event_date}-${m.kind}-${m.title}`}
              className="relative"
              data-testid={`readiness-milestone-${m.milestone_id || m.event_date}`}
            >
              {/* Node on the timeline */}
              <span className="absolute -left-[31px] mt-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-white bg-ek-purple shadow" />
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-ek-ink/50">
                  {fmtDate(m.event_date)}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                    TONE_CLS[km.tone] || TONE_CLS.purple
                  }`}
                >
                  {km.label}
                </span>
              </div>
              <p className="mt-1 font-display text-sm font-semibold text-ek-ink">
                {m.title}
              </p>
              {m.detail && (
                <p className="mt-1 max-w-3xl text-sm text-ek-ink/70">{m.detail}</p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
